import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with automatic retry + full-page reload for stale chunks.
 *
 * After a deployment, old chunk filenames no longer exist on the server.
 * Dynamic imports for those chunks fail with "Failed to fetch dynamically
 * imported module" or "Importing a module script failed".
 *
 * This helper:
 *  1. Retries the import up to `maxRetries` times with exponential back-off.
 *  2. If all retries fail, forces a single full-page reload so the browser
 *     fetches the new index.html (with updated chunk references).
 *  3. Uses sessionStorage to prevent infinite reload loops.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  chunkName?: string
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(importFn, chunkName));
}

async function retryImport<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  chunkName?: string,
  maxRetries = 2
): Promise<{ default: T }> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await importFn();
    } catch (error) {
      if (attempt < maxRetries) {
        // Exponential back-off: 500ms, 1000ms
        await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)));
        continue;
      }

      // All retries exhausted — try a full reload (once)
      const storageKey = `chunk-reload:${chunkName || "page"}`;
      const alreadyReloaded = sessionStorage.getItem(storageKey);

      if (!alreadyReloaded) {
        sessionStorage.setItem(storageKey, "1");
        window.location.reload();
        // Return a never-resolving promise so React doesn't render the error
        // boundary while the page is reloading.
        return new Promise(() => {});
      }

      // Already reloaded once — clear flag and let the error propagate
      // so the error boundary can handle it normally.
      sessionStorage.removeItem(storageKey);
      throw error;
    }
  }

  // TypeScript exhaustiveness — unreachable
  throw new Error("Unreachable");
}
